import httpClient from '@/lib/httpClient'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type { BillingSubscription } from './useGetBillingSubscription'

/** plan_page | billing_page | a feature lookup_key (e.g. money-paths.advanced) */
export type EarlyAccessSource = string

const BILLING_SUBSCRIPTION = ['billing-subscription']

type Snapshot = [readonly unknown[], BillingSubscription | undefined][]

/**
 * One-click Pro early access request. The status is per user, so it is set
 * optimistically on every cached space's billing payload and rolled back if
 * the request fails.
 */
export default function useRequestEarlyAccess() {
  const queryClient = useQueryClient()

  const setStatus = (status: 'requested' | null) =>
    queryClient.setQueriesData<BillingSubscription>(
      { queryKey: BILLING_SUBSCRIPTION },
      (data) => (data ? { ...data, early_access: { status } } : data),
    )

  return useMutation<
    { status: string; requested_at: string },
    unknown,
    EarlyAccessSource,
    { snapshot: Snapshot }
  >({
    mutationFn: async (source) => {
      const response = await httpClient.post(
        'billing/early-access',
        { source },
        { silentErrors: true },
      )
      return response.data
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: BILLING_SUBSCRIPTION })
      const snapshot = queryClient.getQueriesData<BillingSubscription>({
        queryKey: BILLING_SUBSCRIPTION,
      })
      setStatus('requested')
      return { snapshot }
    },
    onError: (_error, _source, context) => {
      context?.snapshot.forEach(([key, data]) =>
        queryClient.setQueryData(key, data),
      )
      toast.error("Couldn't send your request — try again")
    },
  })
}
